<script lang="ts">
export default {
  name: 'ModernColumnFilter',
};
</script>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from 'vue';
import { Columns3 } from '@lucide/vue';
import AppButton from '@/components/v2/AppButton.vue';

const DROPDOWN_OFFSET = 4;
const MENU_MAX_HEIGHT = 500;

interface Field {
  value: string;
  text: string;
  stack: string;
  comparer?: (direction: 'ascending' | 'descending') => (a: any, b: any) => number;
}

const props = withDefaults(
  defineProps<{
    defaultFields: string[];
    appliedFields?: string[];
    fields: Field[];
  }>(),
  {
    appliedFields: undefined,
  },
);

const emit = defineEmits<{
  change: [fields: string[]];
}>();

// State
const menu = ref(false);
const searchQuery = ref('');
const localSelectedFields = ref<string[]>([]);
const dirty = ref(false);
const activeCategory = ref('');
const menuRef = ref<HTMLElement | null>(null);
const activatorRef = ref<HTMLElement | null>(null);
const dropdownPosition = ref({ top: 0, left: 0 });
let clickOutsideCleanup: (() => void) | null = null;

// Computed
const selectedFields = computed(() => {
  return props.appliedFields || props.defaultFields || [];
});

const selectedCount = computed(() => {
  // When menu is open, show the current selection count
  // Otherwise show the applied fields count
  return menu.value ? localSelectedFields.value.length : selectedFields.value.length;
});

const localSelectedCount = computed(() => {
  return localSelectedFields.value.length;
});

const stacks = computed(() => {
  const uniqueStacks = [...new Set(props.fields.map((f) => f.stack))];
  return ['All Columns', ...uniqueStacks];
});

const activeFields = computed(() => {
  if (!activeCategory.value) return [];
  if (activeCategory.value === 'All Columns') {
    return props.fields;
  }
  return props.fields.filter((f) => f.stack === activeCategory.value);
});

const filteredActiveFields = computed(() => {
  if (!searchQuery.value) return activeFields.value;
  const query = searchQuery.value.toLowerCase();
  return activeFields.value.filter((field) => field.text.toLowerCase().includes(query));
});

const isCategoryAllSelected = computed(() => {
  if (activeFields.value.length === 0) return false;
  return activeFields.value.every((field) => localSelectedFields.value.includes(field.value));
});

const isCategoryIndeterminate = computed(() => {
  if (activeFields.value.length === 0) return false;
  const selectedInCategory = activeFields.value.filter((field) => localSelectedFields.value.includes(field.value));
  return selectedInCategory.length > 0 && selectedInCategory.length < activeFields.value.length;
});

onMounted(() => {
  localSelectedFields.value = [...selectedFields.value];
});

// Watch
watch(menu, (val) => {
  if (val) {
    // When opening, sync local state with applied fields
    localSelectedFields.value = [...selectedFields.value];
    dirty.value = false; // Reset dirty flag when opening
    searchQuery.value = '';
    activeCategory.value = stacks.value[0] || '';
    // Attach click-outside after the opening click has fully completed
    const close = (e: MouseEvent) => {
      const el = e.target as Node;
      if (menuRef.value?.contains(el) || activatorRef.value?.contains(el)) {
        return;
      }
      menu.value = false;
      document.removeEventListener('click', close, true);
      clickOutsideCleanup = null;
    };
    clickOutsideCleanup = () => {
      document.removeEventListener('click', close, true);
    };
    nextTick(() => {
      document.addEventListener('click', close, true);
    });
  } else {
    clickOutsideCleanup?.();
    clickOutsideCleanup = null;
  }
});

watch(localSelectedFields, () => {
  dirty.value = true;
});

function resetToDefaults() {
  localSelectedFields.value = [...props.defaultFields];
}

function toggleCategorySelectAll() {
  const categoryFieldValues = activeFields.value.map((f) => f.value);
  if (isCategoryAllSelected.value) {
    // Remove all category fields from selection
    localSelectedFields.value = localSelectedFields.value.filter((val) => !categoryFieldValues.includes(val));
  } else {
    // Add all category fields to selection
    const newValues = categoryFieldValues.filter((val) => !localSelectedFields.value.includes(val));
    localSelectedFields.value = [...localSelectedFields.value, ...newValues];
  }
}

function getCategorySelectionCount(category: string) {
  const categoryFields = category === 'All Columns' ? props.fields : props.fields.filter((f) => f.stack === category);
  return categoryFields.filter((field) => localSelectedFields.value.includes(field.value)).length;
}

function cancel() {
  localSelectedFields.value = [...selectedFields.value];
  dirty.value = false; // Reset dirty flag when canceling
  menu.value = false;
}

function apply() {
  if (dirty.value && localSelectedFields.value.length > 0) {
    emit('change', localSelectedFields.value);
  }
  menu.value = false;
}

function updateDropdownPosition() {
  const el = activatorRef.value;
  if (!el) return;
  const rect = el.getBoundingClientRect();
  const viewportHeight = window.innerHeight;
  const menuMaxHeight = Math.min(MENU_MAX_HEIGHT, viewportHeight * 0.7);
  const spaceBelow = viewportHeight - rect.bottom - DROPDOWN_OFFSET;
  const spaceAbove = rect.top - DROPDOWN_OFFSET;

  let top: number;
  if (spaceBelow >= menuMaxHeight || spaceBelow >= spaceAbove) {
    top = rect.bottom + DROPDOWN_OFFSET;
  } else {
    top = rect.top - menuMaxHeight - DROPDOWN_OFFSET;
  }
  top = Math.max(DROPDOWN_OFFSET, Math.min(top, viewportHeight - menuMaxHeight - DROPDOWN_OFFSET));

  const menuWidth = 650;
  let left = rect.left;
  left = Math.max(DROPDOWN_OFFSET, Math.min(left, window.innerWidth - menuWidth - DROPDOWN_OFFSET));

  dropdownPosition.value = { top, left };
}

function toggleMenu() {
  if (!menu.value) {
    nextTick(updateDropdownPosition);
  }
  menu.value = !menu.value;
}

onUnmounted(() => {
  clickOutsideCleanup?.();
});
</script>

<template>
  <div class="modern-filter-button relative p-1">
    <button
      ref="activatorRef"
      type="button"
      class="inline-flex cursor-pointer items-center gap-2 rounded-md border border-gray-300 bg-white px-3 py-1.5 text-left select-none hover:bg-gray-50"
      @click.prevent.stop="toggleMenu"
      data-testid="column-filter-activator">
      <Columns3 class="h-4 w-4 stroke-primary" />
      <span class="text-sm font-medium">Columns</span>
      <span class="badge border-gray-400 bg-base-200 badge-sm">{{ selectedCount }}</span>
    </button>

    <Teleport to="body">
      <Transition name="filter-popup-slide">
        <div
          v-if="menu"
          ref="menuRef"
          class="mint-filter-popup fixed z-1000 flex max-h-[70vh] flex-col overflow-hidden rounded-lg border border-gray-300 bg-white shadow-xl"
          :style="{ top: `${dropdownPosition.top}px`, left: `${dropdownPosition.left}px` }"
          @click.stop>
        <!-- Header -->
        <div class="flex shrink-0 items-center justify-between border-b border-gray-200 p-4">
          <h3 class="text-lg font-semibold">Columns</h3>
          <div class="flex items-center gap-4">
            <span class="text-sm text-gray-600">{{ localSelectedCount }} columns selected</span>
            <AppButton
              @click="resetToDefaults"
              data-testid="column-filter-reset">
              Reset
            </AppButton>
          </div>
        </div>

        <div class="flex min-h-0 flex-1 overflow-hidden">
          <!-- Left sidebar -->
          <div class="w-48 shrink-0 overflow-y-auto border-r border-gray-200 bg-gray-50 select-none">
            <div class="p-3">
              <div
                v-for="stack in stacks"
                data-testid="column-filter-category"
                :key="stack"
                @click="activeCategory = stack"
                class="cursor- mb-1 flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm"
                :class="[
                  activeCategory === stack ? 'bg-blue-100 font-medium text-blue-700' : 'text-gray-700 hover:bg-gray-100',
                  stack !== 'All Columns' ? 'ml-4' : '',
                ]">
                <span>{{ stack }}</span>
                <span
                  v-if="getCategorySelectionCount(stack) > 0"
                  class="inline-flex h-5 min-w-[20px] items-center justify-center rounded-full border border-solid border-gray-400 bg-base-200 px-1.5 text-xs font-medium">
                  {{ getCategorySelectionCount(stack) }}
                </span>
              </div>
            </div>
          </div>

          <!-- Right content -->
          <div class="flex min-h-0 flex-1 flex-col overflow-hidden">
            <!-- Search bar -->
            <div class="shrink-0 border-b border-gray-200 p-4">
              <div class="relative">
                <svg
                  class="absolute top-2.5 left-3 h-4 w-4 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
                </svg>
                <input
                  type="text"
                  v-model="searchQuery"
                  placeholder="Search categories..."
                  class="w-full rounded-md border border-solid border-gray-300 bg-gray-50 py-2 pr-3 pl-9 text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                  data-testid="column-filter-search" />
              </div>
            </div>

            <!-- Content area -->
            <div class="flex-1 overflow-y-auto p-4">
              <!-- Select all -->
              <label class="mb-4 flex cursor-pointer items-center gap-3">
                <input
                  type="checkbox"
                  :checked="isCategoryAllSelected"
                  :indeterminate="isCategoryIndeterminate"
                  @change="toggleCategorySelectAll"
                  class="h-4 w-4 rounded border-gray-300" />
                <span class="text-sm text-gray-700">Select all</span>
              </label>

              <!-- Fields list -->
              <label
                v-for="field in filteredActiveFields"
                :key="field.value"
                class="flex cursor-pointer items-center gap-3 rounded-md p-2 hover:bg-gray-50">
                <input
                  type="checkbox"
                  v-model="localSelectedFields"
                  :value="field.value"
                  class="h-4 w-4 rounded border-gray-300" />
                <span class="flex flex-1 items-center gap-2 text-sm text-gray-700">{{ field.text }}</span>
              </label>

              <!-- No results -->
              <div
                v-if="filteredActiveFields.length === 0"
                class="py-8 text-center text-gray-500">
                No fields match your search
              </div>
            </div>

            <!-- Footer -->
            <div class="flex shrink-0 justify-end gap-3 border-t border-gray-200 p-4">
              <AppButton @click="cancel"> Cancel </AppButton>
            <AppButton
              primary
              data-testid="column-filter-apply"
              @click="apply">
                Apply
              </AppButton>
            </div>
          </div>
        </div>
      </div>
      </Transition>
    </Teleport>
  </div>
</template>

<style scoped>
.modern-filter-button {
  display: inline-block;
}

.mint-filter-popup {
  width: 650px;
}

/* Slide in from the right */
.filter-popup-slide-enter-active,
.filter-popup-slide-leave-active {
  transition: transform 0.2s ease-out, opacity 0.2s ease-out;
}

.filter-popup-slide-enter-from,
.filter-popup-slide-leave-to {
  transform: translateX(-1rem);
  opacity: 0;
}

.filter-popup-slide-enter-to,
.filter-popup-slide-leave-from {
  transform: translateX(0);
  opacity: 1;
}

/* Customize checkbox appearance */
input[type='checkbox'] {
  cursor: pointer;
}

input[type='checkbox']:checked {
  background-color: #3b82f6;
  border-color: #3b82f6;
}

input[type='checkbox']:indeterminate {
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='white' viewBox='0 0 16 16'%3e%3cpath d='M5 8h6'/%3e%3c/svg%3e");
  background-color: #3b82f6;
  border-color: #3b82f6;
}
</style>
